import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { encode } from 'uqr';
import { Button } from './controls.tsx';

/**
 * What a page shows to be scanned, read or copied: a QR code, a link that
 * wraps only between its parts, and copying it.
 */

const QUIET_MODULES = 4;
const COPIED_FOR_MS = 2000;

/** The QR's modules as one SVG path, `size` modules square with the quiet zone. */
function qrPath(value: string): { readonly size: number; readonly path: string } {
  const qr = encode(value, { ecc: 'M', border: QUIET_MODULES });
  const runs: string[] = [];
  qr.data.forEach((row, y) => {
    row.forEach((dark, x) => {
      if (dark) runs.push(`M${String(x)} ${String(y)}h1v1h-1z`);
    });
  });
  return { size: qr.size, path: runs.join('') };
}

const PRINT_PX = 1024;

/** Hands the person the QR of `value` as a file to print: SVG (sharp at any size) or a 1024 px PNG. */
export async function saveQr(value: string, name: string, kind: 'svg' | 'png'): Promise<void> {
  const { size, path } = qrPath(value);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${String(size)} ${String(size)}" width="${String(PRINT_PX)}" height="${String(PRINT_PX)}" shape-rendering="crispEdges"><rect width="${String(size)}" height="${String(size)}" fill="#fff"/><path d="${path}" fill="#000"/></svg>`;
  if (kind === 'svg') {
    saveFile(`${name}.svg`, svg, 'image/svg+xml');
    return;
  }
  const picture = new Image();
  picture.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  await picture.decode();
  const canvas = document.createElement('canvas');
  canvas.width = PRINT_PX;
  canvas.height = PRINT_PX;
  canvas.getContext('2d')?.drawImage(picture, 0, 0, PRINT_PX, PRINT_PX);
  const url = canvas.toDataURL('image/png');
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name}.png`;
  a.click();
}

/** A QR code of `value` as SVG: dark modules on white with the quiet zone, whatever the screen's ground, since scanners need that. `px` wide, or the width of its box. */
export function QrCode({ value, label, testId, px }: { readonly value: string; readonly label: string; readonly testId: string; readonly px?: number }) {
  const { size, path } = useMemo(() => qrPath(value), [value]);
  return (
    <svg className={`block rounded-control border border-line${px === undefined ? ' h-auto w-full' : ''}`} role="img" aria-label={label} data-testid={testId} viewBox={`0 0 ${String(size)} ${String(size)}`} width={px} height={px} shapeRendering="crispEdges">
      <rect width={size} height={size} fill="#fff" />
      <path d={path} fill="#000" />
    </svg>
  );
}

/** A URL that wraps at its slashes and query marks, and inside a part only when the part alone is wider than the line. `<wbr>` adds nothing to copy. */
export function Url({ url }: { readonly url: string }) {
  const parts = url.split(/(?<=\/)|(?=[?&#])/);
  return (
    <span className="min-w-0 [overflow-wrap:anywhere]">
      {parts.map((part, at) => (
        <Fragment key={at}>
          {at > 0 && <wbr />}
          {part}
        </Fragment>
      ))}
    </span>
  );
}

/** One cell of a spreadsheet: words, a number, or nothing. */
export type SheetCell = string | number | null;

/**
 * Hands the person a spreadsheet, `name`.xlsx: the first row the column
 * names (bold, frozen above the rest), then one row per record. The writer
 * (write-excel-file) loads only when a sheet is asked for.
 */
export async function saveXlsx(name: string, rows: readonly (readonly SheetCell[])[]): Promise<void> {
  const { default: writeXlsx } = await import('write-excel-file/browser');
  const [head = [], ...body] = rows;
  const data = [head.map((value) => (value === null ? null : { value, fontWeight: 'bold' as const })), ...body.map((row) => row.map((value) => (value === null ? null : { value })))];
  const widths = head.map((_, at) => ({ width: Math.min(40, Math.max(10, ...rows.map((row) => String(row[at] ?? '').length + 2))) }));
  await writeXlsx(data, { columns: widths, stickyRowsCount: 1 }).toFile(`${name}.xlsx`);
}

/** Hands the person `text` as a file called `name` (the browser's download). */
export function saveFile(name: string, text: string, type: string): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/** Copying `link` to the clipboard: "copied" for two seconds, or "failed" with the link selected in `output` for the person to copy. */
export function useCopy(link: string | null, output: RefObject<HTMLElement | null>): readonly ['idle' | 'copied' | 'failed', () => Promise<void>, () => void] {
  const [copy, setCopy] = useState<'idle' | 'copied' | 'failed'>('idle');

  useEffect(() => {
    if (copy !== 'copied') return;
    const timer = setTimeout(() => {
      setCopy('idle');
    }, COPIED_FOR_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [copy]);

  const copyLink = async () => {
    if (link === null) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopy('copied');
    } catch {
      if (output.current !== null) getSelection()?.selectAllChildren(output.current);
      setCopy('failed');
    }
  };

  const reset = () => {
    setCopy('idle');
  };

  return [copy, copyLink, reset];
}

/**
 * A link to hand out: the address (wrapping between its parts), a copy
 * button that says when it copied, and its QR code with a caption. When the
 * clipboard refuses, the address is selected and `failed` says to copy it.
 * `poster` is the code to print or show on a screen: the QR first and
 * large, the address under it, then the buttons (copy and `actions`); on
 * paper only the code and the address. `qrLink` is what the QR holds when
 * it differs from the address to copy (a link that says it was scanned).
 */
export function ShareLink({
  link,
  qrLink = link,
  testId,
  words,
  qr,
  look = 'inline',
  actions,
}: {
  readonly link: string;
  readonly qrLink?: string;
  readonly testId: string;
  readonly words: { readonly copy: string; readonly copied: string; readonly failed: string };
  readonly qr?: { readonly label: string; readonly caption?: string; readonly testId: string };
  readonly look?: 'inline' | 'poster';
  readonly actions?: ReactNode;
}) {
  const output = useRef<HTMLOutputElement>(null);
  const [copy, copyLink, reset] = useCopy(link, output);
  // A new link is not copied yet.
  useEffect(reset, [link]);
  const copyButton = (
    <Button variant="secondary" onClick={() => void copyLink()}>
      {copy === 'copied' ? words.copied : words.copy}
    </Button>
  );
  const failed = copy === 'failed' && (
    <p className="text-late-ink" role="status">
      {words.failed}
    </p>
  );
  if (look === 'poster')
    return (
      <div className="flex flex-col items-center gap-s4 text-center">
        {qr !== undefined && <QrCode value={qrLink} label={qr.label} testId={qr.testId} px={240} />}
        <output ref={output} className="max-w-full rounded-control bg-sunk px-s3 py-s2 font-medium text-ink select-all" data-testid={testId}>
          <Url url={link} />
        </output>
        {failed}
        <div className="flex flex-wrap justify-center gap-s3">
          {copyButton}
          {actions}
        </div>
      </div>
    );
  return (
    <div className="flex flex-col gap-s4">
      <div className="flex flex-wrap items-center gap-s3">
        <output ref={output} className="min-w-0 flex-1 rounded-control bg-sunk px-s3 py-s2 font-medium text-ink select-all" data-testid={testId}>
          <Url url={link} />
        </output>
        {copyButton}
        {actions}
      </div>
      {failed}
      {qr !== undefined && (
        <figure className="m-0 flex flex-col items-start gap-s2">
          <QrCode value={qrLink} label={qr.label} testId={qr.testId} px={200} />
          {qr.caption !== undefined && <figcaption className="text-caption text-muted">{qr.caption}</figcaption>}
        </figure>
      )}
    </div>
  );
}
